// src/components/api/krs/krsApi.js

/* ---------------- Env + BASE ---------------- */
const viteDev =
  typeof import.meta !== "undefined" &&
  import.meta.env &&
  import.meta.env.DEV === true;

const craDev =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "development";

// Turn proxy on explicitly in your env:
// Vite: VITE_USE_PROXY=true
// CRA:  REACT_APP_USE_PROXY=true
const useProxy =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    String(import.meta.env.VITE_USE_PROXY) === "true") ||
  (typeof process !== "undefined" &&
    process.env &&
    String(process.env.REACT_APP_USE_PROXY) === "true");

const BASE =
  (viteDev || craDev) && useProxy
    ? "/krs-api/api/Krs"
    : "https://api-krs.ms.gov.pl/api/Krs";

/* ---------------- Fetchers ---------------- */
export async function fetchBulletin(dzien, godzinaOd, godzinaDo) {
  const url = `${BASE}/Biuletyn/${dzien}?godzinaOd=${godzinaOd}&godzinaDo=${godzinaDo}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Bulletin request failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchCurrentExtract(krs) {
  const url = `${BASE}/OdpisAktualny/${encodeURIComponent(krs)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Current extract failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchFullExtract(krs) {
  const url = `${BASE}/OdpisPelny/${encodeURIComponent(krs)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Full extract failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchCurrentByNip(nip) {
  const url = `${BASE}/OdpisAktualny?nip=${encodeURIComponent(nip)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Current-by-NIP failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchFullByNip(nip) {
  const url = `${BASE}/OdpisPelny?nip=${encodeURIComponent(nip)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Full-by-NIP failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/* ---------------- Helpers ---------------- */
const get = (obj, path, def) =>
  path.split(".").reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj) ?? def;

const first = (v) => (Array.isArray(v) ? v[0] : v);

function formatAddress(obj) {
  if (!obj || typeof obj === "string") return obj || "";
  const parts = [
    obj.ulica,
    obj.nrDomu,
    obj.nrLokalu,
    obj.miejscowosc,
    obj.kodPocztowy,
    obj.poczta,
    obj.kraj,
  ].filter(Boolean);
  return parts.join(", ");
}

/* ---------------- Normalizer (Aktualny + Pełny) ---------------- */
/**
 * Normalizes both "Aktualny" and "Pełny" JSONs into a flat display model:
 * {
 *   rodzaj, krs, dataOdpisu, stanNa, nazwa, nip, regon,
 *   formaPrawna, adres, woj, powiat, gmina, miejscowosc,
 *   sposobReprezentacji, zarzad[], nadzor[], pkdPrzewazajacy, pkdPozostale[]
 * }
 */
export function normalizeExtract(json) {
  const rodzaj = get(json, "odpis.rodzaj", "");

  // --- headers ---
  const krs =
    get(json, "odpis.naglowekA.numerKRS") || // Aktualny
    get(json, "odpis.naglowekP.numerKRS") || // Pełny
    get(json, "odpis.naglowek.numerKRS", "");

  const dataOdpisu =
    get(json, "odpis.naglowekA.dataCzasOdpisu") ||
    get(json, "odpis.naglowekP.dataCzasOdpisu") ||
    get(json, "odpis.naglowek.dataCzasOdpisu", "");

  const stanNa =
    get(json, "odpis.naglowekA.stanZDnia") ||
    get(json, "odpis.naglowekP.stanZDnia") ||
    get(json, "odpis.naglowek.stanZDnia", "");

  // --- Aktualny structure (mostly flat) ---
  const A_danePod = get(json, "odpis.dane.dzial1.danePodmiotu");
  const A_ident   = A_danePod?.identyfikatory || {};
  const A_siedz   = get(json, "odpis.dane.dzial1.siedzibaIAdres.siedziba", {});
  const A_adres   = get(json, "odpis.dane.dzial1.siedzibaIAdres.adres", {});
  const A_repr    = get(json, "odpis.dane.dzial2.reprezentacja", {});
  const A_przew   = get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPrzewazajacejDzialalnosci.0", {});
  const A_poz     = get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPozostalejDzialalnosci", []);

  // --- Pełny structure (arrays/wrappers) ---
  const P_d1       = get(json, "odpis.dane.dzial1", {}) || {};
  const P_danePod  = P_d1?.danePodmiotu || {};
  const P_nazwa    = (Array.isArray(P_danePod?.nazwa) ? P_danePod.nazwa[0]?.nazwa : P_danePod?.nazwa) || "";
  const P_forma    = (Array.isArray(P_danePod?.formaPrawna) ? P_danePod.formaPrawna[0]?.formaPrawna : P_danePod?.formaPrawna) || "";

  // identyfikatory can be: array of wrappers; object with 'identyfikatory'; or flat
  let P_ident = {};
  if (Array.isArray(P_danePod?.identyfikatory)) {
    const arr = P_danePod.identyfikatory;
    const hit =
      arr.find((x) => x?.identyfikatory?.nip || x?.identyfikatory?.regon) ||
      arr[0];
    P_ident = hit?.identyfikatory || {};
  } else if (P_danePod?.identyfikatory) {
    P_ident = P_danePod.identyfikatory.identyfikatory || P_danePod.identyfikatory;
  }

  const P_siedz = Array.isArray(get(P_d1, "siedzibaIAdres.siedziba"))
    ? get(P_d1, "siedzibaIAdres.siedziba")[0] || {}
    : get(P_d1, "siedzibaIAdres.siedziba", {}) || {};

  const P_adres = Array.isArray(get(P_d1, "siedzibaIAdres.adres"))
    ? get(P_d1, "siedzibaIAdres.adres")[0] || {}
    : get(P_d1, "siedzibaIAdres.adres", {}) || {};

  const P_repr = Array.isArray(get(json, "odpis.dane.dzial2.reprezentacja"))
    ? get(json, "odpis.dane.dzial2.reprezentacja")[0]
    : get(json, "odpis.dane.dzial2.reprezentacja", {}) || {};

  const P_reprSpos = Array.isArray(P_repr?.sposobReprezentacji)
    ? P_repr.sposobReprezentacji[0]?.sposobReprezentacji
    : P_repr?.sposobReprezentacji;

  const P_reprSklad = Array.isArray(P_repr?.sklad) ? P_repr.sklad : [];

  const P_nadzor = Array.isArray(get(json, "odpis.dane.dzial2.organNadzoru"))
    ? get(json, "odpis.dane.dzial2.organNadzoru")[0]
    : get(json, "odpis.dane.dzial2.organNadzoru", {}) || {};

  const P_nadzorSklad = Array.isArray(P_nadzor?.sklad) ? P_nadzor.sklad : [];

  const P_przew = Array.isArray(get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPrzewazajacejDzialalnosci"))
    ? get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPrzewazajacejDzialalnosci")[0]
    : get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPrzewazajacejDzialalnosci", {}) || {};

  const P_przewPoz = Array.isArray(P_przew?.pozycja) ? P_przew.pozycja[0] : P_przew?.pozycja || {};

  const P_poz = get(json, "odpis.dane.dzial3.przedmiotDzialalnosci.przedmiotPozostalejDzialalnosci", []);

  // --- pick values (Aktualny first, then Pełny fallback) ---
  const nazwa =
    A_danePod?.nazwa || P_nazwa || "";

  const nip =
    A_ident?.nip || P_ident?.nip || "";

  const regon =
    A_ident?.regon || P_ident?.regon || "";

  const formaPrawna =
    A_danePod?.formaPrawna || P_forma || "";

  const woj =
    A_siedz?.wojewodztwo || P_siedz?.wojewodztwo || "";

  const powiat =
    A_siedz?.powiat || P_siedz?.powiat || "";

  const gmina =
    A_siedz?.gmina || P_siedz?.gmina || "";

  const miejscowosc =
    A_siedz?.miejscowosc || P_siedz?.miejscowosc || "";

  const adres =
    formatAddress(A_adres) || formatAddress(P_adres) || "";

  const sposobReprezentacji =
    A_repr?.sposobReprezentacji || P_reprSpos || "";

  // map people (names masked by API; we keep structure)
  const mapOsoba = (o) => {
    const nazw  = first(o?.nazwisko)?.nazwisko?.nazwiskoICzlon || first(o?.nazwiskoICzlon) || "";
    const im    = first(o?.imiona)?.imiona?.imie || first(o?.imie) || "";
    const im2   = first(o?.imiona)?.imiona?.imieDrugie || first(o?.imieDrugie) || "";
    const funk  = first(o?.funkcjaWOrganie)?.funkcjaWOrganie || "";
    return [[im, im2].filter(Boolean).join(" "), nazw, funk].filter(Boolean).join(" · ");
  };

  const A_zarzadArr = Array.isArray(A_repr?.sklad) ? A_repr.sklad : [];
  const P_zarzadArr = P_reprSklad;

  const zarzad = (A_zarzadArr.length ? A_zarzadArr : P_zarzadArr).map(mapOsoba);
  const nadzor = P_nadzorSklad.map(mapOsoba);

  // PKD
  const pkdPrzewazajacy =
    first(A_przew?.opis) || first(P_przewPoz?.opis) || "";

  const pkdPozostale = [
    ...(Array.isArray(A_poz) ? A_poz.map((x) => x?.opis).filter(Boolean) : []),
    ...(Array.isArray(P_poz)
      ? P_poz.flatMap((blok) => (Array.isArray(blok?.pozycja) ? blok.pozycja : []))
            .map((p) => p?.opis)
            .filter(Boolean)
      : []),
  ];

  return {
    rodzaj, krs, dataOdpisu, stanNa,
    nazwa, nip, regon, formaPrawna,
    adres, woj, powiat, gmina, miejscowosc,
    sposobReprezentacji, zarzad, nadzor,
    pkdPrzewazajacy, pkdPozostale,
  };
}
